"use client";

import type { LeadFlowStepDefinition } from "../types";

import { useEffect, useRef } from "react";

type UseLeadFlowInterstitialAutoAdvanceInput = {
  advance: () => void;
  currentStep: LeadFlowStepDefinition | undefined;
  currentStepIndex: number;
  /** False while the step can't move on, such as on the last step. */
  isEnabled: boolean;
};

/**
 * Moves an auto-advancing interstitial on after its duration. It runs
 * when the step is entered going forward or directly, and not when Back (the
 * in-form link or the browser's) lands on it, so the visitor continues
 * manually from there.
 */
function useInterstitialAutoAdvance({
  advance,
  currentStep,
  currentStepIndex,
  isEnabled,
}: UseLeadFlowInterstitialAutoAdvanceInput) {
  const previousStepIndexRef = useRef<number | null>(null);
  const isEnteredBackwardRef = useRef(false);
  // The timer reads the latest advance, so a new callback identity (it
  // changes with answers and validation) doesn't restart the countdown.
  const advanceRef = useRef(advance);

  useEffect(() => {
    advanceRef.current = advance;
  }, [advance]);

  useEffect(() => {
    const previousStepIndex = previousStepIndexRef.current;
    if (previousStepIndex !== currentStepIndex) {
      isEnteredBackwardRef.current =
        previousStepIndex !== null && currentStepIndex < previousStepIndex;
      previousStepIndexRef.current = currentStepIndex;
    }

    if (
      !isEnabled ||
      isEnteredBackwardRef.current ||
      currentStep?._type !== "leadFlowInterstitialStep"
    ) {
      return;
    }

    const durationSeconds = currentStep.autoAdvance?.enabled
      ? currentStep.autoAdvance.durationSeconds
      : null;
    if (!durationSeconds) {
      return;
    }

    const timeout = setTimeout(
      () => advanceRef.current(),
      durationSeconds * 1000,
    );

    return () => clearTimeout(timeout);
  }, [currentStep, currentStepIndex, isEnabled]);
}

export { useInterstitialAutoAdvance };
